import { createContext, useContext, useEffect, useState, useCallback } from "react";

const CLAVE = "tukit_consentimiento_v1";
const ConsentContext = createContext({ estado: "pendiente", aceptar: () => {}, rechazar: () => {} });

export const useConsent = () => useContext(ConsentContext);

function leer() {
  try {
    const v = localStorage.getItem(CLAVE);
    return v === "aceptado" || v === "rechazado" ? v : "pendiente";
  } catch {
    // Modo privado o almacenamiento bloqueado: se trata como pendiente
    // y, por omisión, sin consentimiento.
    return "pendiente";
  }
}

function guardar(v) {
  try {
    localStorage.setItem(CLAVE, v);
  } catch {
    /* sin almacenamiento: la decisión sólo dura esta sesión */
  }
}

/**
 * Consent Mode v2 de Google.
 *
 * Por omisión se deniega todo. AdSense se carga siempre (lo exige para
 * medir), pero sin consentimiento sirve anuncios no personalizados, que
 * es justo lo que la normativa europea pide. `gtag` se define aquí para
 * que las señales existan aunque no haya Analytics instalado todavía.
 */
function aplicarConsentMode(concedido) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = window.gtag || gtag;

  const valor = concedido ? "granted" : "denied";
  window.gtag("consent", "update", {
    ad_storage: valor,
    ad_user_data: valor,
    ad_personalization: valor,
    analytics_storage: valor,
  });
}

export function ConsentProvider({ children }) {
  const [estado, setEstado] = useState("pendiente");

  useEffect(() => {
    const inicial = leer();
    setEstado(inicial);
    if (inicial !== "pendiente") aplicarConsentMode(inicial === "aceptado");
  }, []);

  const decidir = useCallback((v) => {
    guardar(v);
    setEstado(v);
    aplicarConsentMode(v === "aceptado");
  }, []);

  const aceptar = useCallback(() => decidir("aceptado"), [decidir]);
  const rechazar = useCallback(() => decidir("rechazado"), [decidir]);

  return (
    <ConsentContext.Provider value={{ estado, aceptar, rechazar }}>
      {children}
      {estado === "pendiente" && <Banner onAceptar={aceptar} onRechazar={rechazar} />}
    </ConsentContext.Provider>
  );
}

function Banner({ onAceptar, onRechazar }) {
  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Aviso de cookies"
      className="no-imprimir fixed inset-x-0 bottom-0 z-50 p-3 sm:p-4"
    >
      <div className="mx-auto max-w-3xl rounded-2xl border borde superficie p-3.5 shadow-2xl backdrop-blur sm:p-4">
        <p className="texto-2 text-xs leading-relaxed sm:text-sm">
          Usamos cookies propias y de terceros para mostrar publicidad, que es lo que mantiene estas
          calculadoras gratuitas. Puedes rechazarlas: seguirás viendo anuncios, pero no personalizados.{" "}
          <a href="/aviso-de-privacidad" className="acento font-medium underline underline-offset-2">
            Aviso de privacidad
          </a>
          .
        </p>
        <div className="mt-3 flex gap-2 sm:justify-end">
          <button onClick={onRechazar} className="boton boton-suave flex-1 sm:flex-none">
            Sólo lo necesario
          </button>
          <button onClick={onAceptar} className="boton boton-primario flex-1 sm:flex-none">
            Aceptar todo
          </button>
        </div>
      </div>
    </div>
  );
}
